export type Colour = {
    red: number,
    green: number,
    blue: number,
}

export type ColourGradient = {
    start: Colour,
    end: Colour,
}

function isColour(thing: Colour | ColourGradient): thing is Colour {
    return (thing as Colour).red !== undefined;
}

function isColourGradient(thing: Colour | ColourGradient): thing is ColourGradient {
    return (thing as ColourGradient).start !== undefined;
}

export type ColourRange = {
    colour: Colour | ColourGradient,
    startNumber: number,
    endNumber: number,
    scale: 'log' | 'linear',
}

const htmlColourRegex = /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/;
export function parseColour(hex: string): Colour | null {
    const match = hex.match(htmlColourRegex);
    if (!match) return null;
    return {
        red: parseInt(match[1], 16),
        green: parseInt(match[2], 16),
        blue: parseInt(match[3], 16),
    };
}

export function colourToHtml(colour: Colour): string {
    const toHexChars = (number: number) => {
        return Math.round(number).toString(16).padStart(2, '0');
    };
    return `#${toHexChars(colour.red)}${toHexChars(colour.green)}${toHexChars(colour.blue)}`;
}

export default function colourGradient(ranges: ColourRange[]): (number: number) => Colour {
    return (number: number) => {
        const index = ranges.findIndex(it => it.endNumber >= number);
        const { colour, scale, startNumber, endNumber } = index === -1 ? ranges[ranges.length-1] : ranges[index];
        if (isColour(colour)) return colour;
        else {
            const pos = ((number: number) => {
                switch (scale) {
                    case 'linear':
                        return Math.min(Math.max((number - startNumber) / (endNumber - startNumber), 0), 1);
                    case 'log':
                        return Math.min(Math.max(Math.log(number / startNumber) / Math.log(endNumber / startNumber), 0), 1);
                }
            })(number);
            const { start, end } = colour;
            return {
                red: start.red + pos * (end.red - start.red),
                green: start.green + pos * (end.green - start.green),
                blue: start.blue + pos * (end.blue - start.blue),
            };
        }
    };
}